import { Component, HostListener, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule, NavigationEnd } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter, timeout, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { APP_VERSION } from '../../../../core/app-version';
import { PermissionService } from '../../../../core/services/permission.service';
import { StatisticsService } from '../../../../core/services/statistics.service';
import { StoreContextService } from '../../../../core/services/store-context.service';

interface NavItem {
  label: string;
  icon: string;
  route?: string;
  /**
   * v1.6.1574 — query params for a deep link into a tabbed screen, so a tab can
   * be a nav entry without needing its own route.
   */
  queryParams?: Record<string, string>;
  children?: NavItem[];
  expanded?: boolean;
  /** v1.6.936 — resource group gating this item (e.g. 'orders'). Visible to
   *  users with any permission in that group; super admins & users with no
   *  permission data see everything. Untagged items are always visible. */
  permission?: string;
  /** v1.6.968 — when set, render a live count bubble on this item.
   *  'processing' → number of orders in processing status (WooCommerce-style). */
  badge?: 'processing';
  /** Only on catering and split-delivery stores. Read from
   *  `storeCtx.isCateringOrSplit`, so it stays hidden while the store type is
   *  loading or unknown. */
  cateringOrSplitOnly?: boolean;
}

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CommonModule, RouterModule],
    template: `
    <!-- Mobile Backdrop -->
    <div *ngIf="mobileOpen" 
         (click)="closeMobile()"
         class="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden">
    </div>
    
    <!-- Sidebar -->
    <!-- v1.6.329: Restructured from a single overflow-y-auto container
         into a flex column with a scrollable nav region and a fixed
         version footer at the bottom. The version is read from
         core/app-version.ts which is bumped in lockstep with
         package.json on every release. -->
    <div [class.translate-x-0]="mobileOpen"
         class="fixed left-0 top-16 bottom-0 w-64 bg-white border-r border-gray-200 z-50 transform -translate-x-full md:translate-x-0 transition-transform duration-300 flex flex-col">
      <nav class="p-4 flex-1 overflow-y-auto">
        <ul class="space-y-1">
          <ng-container *ngFor="let item of navItems"><li *ngIf="isNavVisible(item)">
            <!-- Parent item without children -->
            <div *ngIf="!item.children" class="nav-item-wrapper">
              <a [routerLink]="item.route" 
                 (click)="closeMobile()"
                 routerLinkActive="bg-[#64d2d3] text-white"
                 [routerLinkActiveOptions]="{exact: false}"
                 class="flex items-center px-4 py-3 text-gray-700 hover:bg-gray-100 rounded-lg transition">
                <i [class]="item.icon + ' w-5'"></i>
                <span class="ml-3 font-medium">{{item.label}}</span>
              </a>
            </div>
            
            <!-- Parent with children -->
            <div *ngIf="item.children">
              <button (click)="toggleItem(item)"
                      class="w-full flex items-center justify-between px-4 py-3 text-gray-700 hover:bg-gray-100 rounded-lg transition">
                <div class="flex items-center">
                  <i [class]="item.icon + ' w-5'"></i>
                  <span class="ml-3">{{item.label}}</span>
                  <span *ngIf="item.badge === 'processing' && processingCount > 0 && !item.expanded"
                        class="ml-2 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold leading-none"
                        title="Orders awaiting processing">{{ processingCount }}</span>
                </div>
                <i class="fa-solid fa-chevron-down text-gray-400 text-xs transition-transform"
                   [class.rotate-180]="item.expanded"></i>
              </button>
              
              <!-- Submenu (Level 2) -->
              <ul *ngIf="item.expanded" class="ml-8 mt-1 space-y-1">
                <ng-container *ngFor="let child of item.children"><li *ngIf="isNavVisible(child)">
                  <!-- Level 2 without children -->
                  <a *ngIf="!child.children"
                     [routerLink]="child.route" [queryParams]="child.queryParams || null"
                     (click)="closeMobile()"
                     routerLinkActive="bg-gray-100 text-[#64d2d3] font-medium"
                     class="flex items-center px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition text-sm">
                    <i [class]="child.icon + ' w-4 text-xs'"></i>
                    <span class="ml-3">{{child.label}}</span>
                    <span *ngIf="child.badge === 'processing' && processingCount > 0"
                          class="ml-auto inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold leading-none"
                          title="Orders awaiting processing">{{ processingCount }}</span>
                  </a>
                  
                  <!-- Level 2 with children (Level 3) -->
                  <div *ngIf="child.children">
                    <button (click)="toggleItem(child)"
                            class="w-full flex items-center justify-between px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition text-sm">
                      <div class="flex items-center">
                        <i [class]="child.icon + ' w-4 text-xs'"></i>
                        <span class="ml-3">{{child.label}}</span>
                      </div>
                      <i class="fa-solid fa-chevron-down text-gray-400 text-xs transition-transform"
                         [class.rotate-180]="child.expanded"></i>
                    </button>
                    
                    <!-- Sub-submenu (Level 3) -->
                    <ul *ngIf="child.expanded" class="ml-8 mt-1 space-y-1">
                      <ng-container *ngFor="let subchild of child.children"><li *ngIf="isNavVisible(subchild)">
                        <a [routerLink]="subchild.route" [queryParams]="subchild.queryParams || null"
                           (click)="closeMobile()"
                           routerLinkActive="bg-gray-100 text-[#64d2d3] font-medium"
                           class="flex items-center px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition text-xs">
                          <i [class]="subchild.icon + ' w-3 text-xs'"></i>
                          <span class="ml-3">{{subchild.label}}</span>
                        </a>
                      </li></ng-container>
                    </ul>
                  </div>
                </li></ng-container>
              </ul>
            </div>
          </li></ng-container>
        </ul>
      </nav>
      <!-- v1.6.329: Version footer pinned to bottom of sidebar.
           Visible on every page so the operator can confirm the
           deployed build matches an expected version when triaging
           "is fix X actually live?" questions. -->
      <div class="border-t border-gray-200 px-4 py-2.5 text-[11px] text-gray-400 font-mono flex items-center justify-between"
           title="Dashboard build version. Bumped on every release.">
        <span class="flex items-center gap-1.5">
          <i class="fa-solid fa-code-branch text-[10px]"></i>
          v{{ appVersion }}
        </span>
      </div>
    </div>
  `,
  styles: []
})
export class SidebarComponent implements OnInit, OnDestroy {
  mobileOpen = false;
  readonly appVersion = APP_VERSION;
  private routerSub!: Subscription;
  private storeSub?: Subscription;

  /** v1.6.968 — Orders-in-processing count for the WooCommerce-style badge on
   *  the Orders nav. Fed by the same /statistics/orders endpoint the dashboard
   *  uses; 0 hides the badge. */
  processingCount = 0;
  /** v1.6.991 — timestamp of the last processing-count fetch (nav-refresh throttle). */
  private _lastCountFetch = 0;

  constructor(
    private router: Router,
    public perms: PermissionService,
    private stats: StatisticsService,
    private storeCtx: StoreContextService,
  ) {}

  ngOnInit() {
    this.routerSub = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        this.mobileOpen = false;
        // v1.6.991 — Re-pull the processing count on navigation so the badge
        // stays current as staff click around without a full page reload
        // (previously it only refreshed on init / store-switch → went stale).
        // Throttled (10s) so rapid navigation doesn't hammer the stats endpoint;
        // any real "sat on a page, an order came in, then navigated" case is well
        // past the window, so it always refreshes then.
        if (Date.now() - this._lastCountFetch > 10000) {
          this.loadProcessingCount(this.storeCtx.snapshot?.id ?? null);
        }
      });

    // v1.6.968 — Refresh the processing count whenever the active store
    // changes (and once on init — context$ is a BehaviorSubject so it emits
    // the current store immediately). Store-scoped, matching the dashboard.
    this.storeSub = this.storeCtx.context$.subscribe(ctx => {
      this.loadProcessingCount((ctx as any)?.id ?? null);
    });
  }

  ngOnDestroy() {
    this.routerSub?.unsubscribe();
    this.storeSub?.unsubscribe();
  }

  /** v1.6.968 — GET /statistics/orders → data.processing_count. Failures are
   *  swallowed to 0 so a stats hiccup never blocks navigation.
   *  v1.6.1390 — added a 10s timeout: the statistics endpoint can hang without ever
   *  responding (backend), which left this XHR pending forever and made the app look stuck on a
   *  spinner. A timed-out or failed stats badge simply shows 0 — it must never block the UI. */
  private loadProcessingCount(storeId?: number | null): void {
    this._lastCountFetch = Date.now();
    this.stats.orders(storeId != null ? { store_id: storeId } : {}).pipe(
      timeout(10000),
      catchError(() => of(null)),
    ).subscribe({
      next: (res: any) => {
        if (res == null) { this.processingCount = 0; return; }
        const data = res?.data ?? res;
        this.processingCount = Math.max(0, +(data?.processing_count ?? 0) || 0);
      },
      error: () => { this.processingCount = 0; },
    });
  }

  /**
   * v1.6.936 — Whether a nav item should be shown for the current user.
   * A parent is shown only if it itself is permitted AND at least one of its
   * children is visible (no empty sections). Leaves are shown if permitted.
   * Super admins / users with no permission data see everything (fail open).
   */
  isNavVisible(item: NavItem): boolean {
    if (!this.perms.canSee(item.permission)) return false;
    if (item.cateringOrSplitOnly && this.storeCtx?.isCateringOrSplit !== true) return false;
    if (item.children && item.children.length) return this.hasVisibleChild(item);
    return true;
  }

  private hasVisibleChild(item: NavItem): boolean {
    return (item.children || []).some(c => this.isNavVisible(c));
  }
  
  navItems: NavItem[] = [
    { label: 'Dashboard', icon: 'fa-solid fa-chart-line', route: '/dashboard' },
    {
      label: 'Orders',
      permission: 'orders',
      icon: 'fa-solid fa-shopping-bag',
      expanded: false,
      badge: 'processing',
      children: [
        { label: 'All Orders', icon: 'fa-solid fa-list', route: '/orders', badge: 'processing' },
        // Same page as the Manual Order button on All Orders; gated only by the
        // Orders group, exactly like that button.
        { label: 'Manual Order', icon: 'fa-solid fa-pen-to-square', route: '/orders/manual/new' },
        { label: 'Bag Scanner', icon: 'fa-solid fa-barcode', route: '/orders/bag-scanner' },
        // v1.6.1467 — Bag Tags (Asana 1217416505643351)
        { label: 'Bag Tags', icon: 'fa-solid fa-tag', route: '/orders/bag-tags' },
        // v1.6.1497 — Asana 1217674676142732
        { label: 'SKU Refresh', icon: 'fa-solid fa-arrows-rotate', route: '/orders/sku-refresh' },
        { label: 'Batch Processor', icon: 'fa-solid fa-boxes', route: '/orders/batch-processor' },
        { label: 'Bag to Points', icon: 'fa-solid fa-coins', route: '/orders/bag-to-points' },
        // v1.6.1092 — recover refunds the gateway rejected.
        { label: 'Failed Refunds', icon: 'fa-solid fa-rotate-right', route: '/orders/failed-refunds' },
        // v1.6.1322 — checkout/cart failure audit listing.
        { label: 'Cart Failures', icon: 'fa-solid fa-cart-shopping', route: '/orders/cart-failures' }
      ]
    },
    {
      label: 'Subscriptions',
      permission: 'subscriptions',
      icon: 'fa-solid fa-rotate',
      expanded: false,
      children: [
        // v1.6.267: hidden per request
        // { label: 'All Subscriptions', icon: 'fa-solid fa-list', route: '/subscriptions' },
        { label: 'Schedules', icon: 'fa-solid fa-calendar-days', route: '/subscriptions/schedules' },
        // v1.6.1772 — the win-back texts themselves. The settings live under
        // Happy Texting and the figures under Analytics, but the LIST belongs
        // here: it is a list of subscriptions' cancellations, and it is gated
        // on subscription.viewAny rather than on a texting permission.
        { label: 'Win-Back Texts', icon: 'fa-solid fa-comment-sms', route: '/subscriptions/win-back-texts' },
        // v1.6.660 — hidden per operator request ("hide cancellation
        // survey tab for now"). Same comment-out pattern as the
        // v1.6.267 hides above so re-enabling later is a one-line
        // uncomment. Route + component remain in place — only the
        // sidebar entry is hidden.
        // { label: 'Cancellation Survey', icon: 'fa-solid fa-clipboard-question', route: '/subscriptions/cancellation-survey' },
        { label: 'Logs', icon: 'fa-solid fa-file-lines', route: '/subscriptions/logs' },
        { label: 'Force-Run History', icon: 'fa-solid fa-bolt', route: '/subscriptions/force-runs' },
        // v1.6.747 — Batches view (Laravel job_batches). Operator-facing
        // monitor for mass-subscription force-runs, replacement scans,
        // and other queued background work.
        { label: 'Batches', icon: 'fa-solid fa-layer-group', route: '/subscriptions/batches' },
        // v1.6.267: hidden per request
        // { label: 'Split Settings', icon: 'fa-solid fa-scissors', route: '/subscriptions/split-settings' },
        { label: 'Settings', icon: 'fa-solid fa-gear', route: '/subscriptions/settings' }
      ]
    },
    {
      label: 'Products',
      permission: 'products',
      icon: 'fa-solid fa-box',
      expanded: false,
      children: [
        { label: 'All Products', icon: 'fa-solid fa-list', route: '/products' },
        { label: 'Add New Product', icon: 'fa-solid fa-plus-circle', route: '/products/new' },
        { label: 'Categories, Tags & Badges', icon: 'fa-solid fa-folder', route: '/products/categories' },
        { label: 'Recipes', icon: 'fa-solid fa-book-open', route: '/products/recipes' },
        { label: 'Product Settings', icon: 'fa-solid fa-sliders', route: '/products/settings' },
        { label: 'Meal Plans', icon: 'fa-solid fa-calendar-days', route: '/meal-plans' },
        { label: 'Global Add-ons', icon: 'fa-solid fa-puzzle-piece', route: '/products/global-addons' },
        { label: 'Menus', icon: 'fa-solid fa-book-open', route: '/products/menus' },
        // Split delivery §5 — catering and split-delivery stores only; the route is guarded too.
        { label: 'Events', icon: 'fa-solid fa-star', route: '/products/events', cateringOrSplitOnly: true },
        /**
         * v1.6.1835 — two calendar entries became one
         * (Asana 1220045966499778, §4.5 and §9).
         *
         * There were two: 'Store Calendar' → /products/store-calendar (the
         * everyday list and the ordering rules) and 'Calendar Schedule' →
         * .../schedule (the month grid). §9 removes both of the first one's
         * cards, so that screen now holds only a note saying where they went,
         * and keeping a nav entry pointing at a page of explanation is worse
         * than one entry pointing at the screen that does the work.
         *
         * So the ENTRY is dropped and the familiar LABEL moves to the real
         * screen. The old route still resolves, so a bookmark or an old link
         * lands on the forwarding note rather than a 404 — which is the half
         * that matters, given 'i dont see X in the sidebar' has been reported
         * twice on this project (v1.6.1770's SEO entry, and Product Links).
         */
        // Catering and split-delivery stores only; the route is guarded too.
        { label: 'Store Calendar', icon: 'fa-solid fa-calendar-days', route: '/products/store-calendar/schedule', cateringOrSplitOnly: true },
        { label: 'Replacements', icon: 'fa-solid fa-arrow-right-arrow-left', route: '/replacements' }
      ]
    },
    /**
     * v1.6.1554 — a group, so the customer screens are reachable from the nav.
     *
     * "New Customers" (v1.6.1539) and "Duplicates" (v1.6.1004) were each a
     * button on the customers LIST and nothing else, so neither existed as far
     * as the sidebar was concerned. Same mistake as the Happy Texting screens
     * in v1.6.1551: shipped, routed, and findable only by accident.
     */
    {
      label: 'Customers',
      permission: 'customers',
      icon: 'fa-solid fa-users',
      expanded: false,
      children: [
        { label: 'All Customers', icon: 'fa-solid fa-list', route: '/customers' },
        { label: 'New Customers', icon: 'fa-solid fa-user-plus', route: '/customers/new' },
        { label: 'Duplicates', icon: 'fa-solid fa-code-merge', route: '/customers/duplicates' },
        // v1.6.1599 — customer segments (Asana 1218604037082530)
        { label: 'Segments', icon: 'fa-solid fa-layer-group', route: '/customers/segments' }
      ]
    },
    /**
     * v1.6.1771 — Asana 1219705554695586, "Add an Icon to the reports tab".
     *
     * There WAS an icon in the code: `fa-file-chart-line`. It is a Font
     * Awesome PRO glyph, and index.html loads the FREE 6.4.0 build from
     * cdnjs — so the <i> rendered an empty box and the row looked iconless
     * next to every other entry.
     *
     * Two more in this file had the same fault and are fixed with it, since
     * it is one bug wearing three hats: Report Settings (the same Pro
     * `fa-file-chart-line`) and Contact Messages (`fa-messages`, Pro; the
     * free build spells it `fa-comments`).
     */
    { label: 'Reports', permission: 'reports', icon: 'fa-solid fa-chart-column', route: '/reports' },
    {
      label: 'Analytics',
      permission: 'analytics',
      icon: 'fa-solid fa-chart-pie',
      expanded: false,
      children: [
        { label: 'Overview', icon: 'fa-solid fa-chart-line', route: '/analytics' },
        { label: 'Real Time', permission: 'store.analytics.view', icon: 'fa-solid fa-signal', route: '/analytics/real-time' },
        { label: 'Product Performance', icon: 'fa-solid fa-box', route: '/analytics/products' },
        { label: 'Category Performance', icon: 'fa-solid fa-folder', route: '/analytics/categories' },
        { label: 'Customer Analytics', icon: 'fa-solid fa-users', route: '/analytics/customers' },
        { label: 'Revenue Breakdown', icon: 'fa-solid fa-dollar-sign', route: '/analytics/revenue' },
        // v1.6.1462 — Tax Report + Meal Reorder Analytics
        { label: 'Tax Report', icon: 'fa-solid fa-receipt', route: '/analytics/tax' },
        // v1.6.1825 — Asana 1220040193164652: the "Tax by City" entry that
        // stood here is GONE. The review is explicit — "Only 'Tax Report'
        // should remain" — and with the tabs removed it pointed at a deep link
        // into a view that no longer exists, so it would have scrolled to a
        // section that is simply part of the one page. The /analytics/tax-by-city
        // route still redirects, so older bookmarks keep working.
        { label: 'Meal Reorders', icon: 'fa-solid fa-rotate-right', route: '/analytics/meal-reorders' },
        { label: 'Discounts & Coupons', icon: 'fa-solid fa-percent', route: '/analytics/discounts' },
        { label: 'Meal Plan Performance', icon: 'fa-solid fa-utensils', route: '/analytics/meal-plans' },
        { label: 'Gift Cards', icon: 'fa-solid fa-gift', route: '/analytics/gift-cards' },
            // v1.6.1663 — Asana 1219140731879631
            { label: 'Points Statements', icon: 'fa-solid fa-envelope-open-text', route: '/marketing/points-statements' },
        { label: 'Referrals', icon: 'fa-solid fa-user-plus', route: '/analytics/referrals' },
        // v1.6.1686 added a second "Points by Date" entry here, kept out of the
        // one above because the two screens reported different things.
        // v1.6.1787 — Asana 1219723661154694 merged the screens, so there is
        // ONE entry again. The second entry is removed rather than repointed:
        // two links to the same route is how an operator ends up asking which
        // of the two is the real one. /analytics/rewards-by-date still resolves
        // for anyone holding the old link — it redirects (see app.routes.ts).
        { label: 'Rewards & Points', icon: 'fa-solid fa-star', route: '/analytics/rewards' },
        { label: 'Abandoned Cart', icon: 'fa-solid fa-cart-arrow-down', route: '/analytics/abandoned-cart' },
        // v1.6.1772 — win-back numbers, next to abandoned cart as asked.
        { label: 'Subscription Win-Back', icon: 'fa-solid fa-rotate-left', route: '/analytics/win-back' },
        // v1.6.1585 — orders by marketing origin (Asana 1218629057611189)
        { label: 'Order Origins', icon: 'fa-solid fa-bullseye', route: '/analytics/order-origins' }
      ]
    },
    /**
     * Happy Logistics is its own TOP-LEVEL section, directly above Happy
     * Texting — the same move v1.6.1551 made for Happy Texting.
     *
     * It sat three levels deep at Settings -> Integrations -> Happy Logistics,
     * behind two collapsed groups. The children are the page's own seven tabs,
     * same labels and same order as `HappyLogisticsComponent.SCREENS`; each
     * deep-links with `?tab=`, which the page reads and keeps in sync.
     *
     * The route stays at /settings/happy-logistics (as Happy Texting's stay
     * under /settings/integrations/...), so bookmarks keep working.
     *
     * `permission: 'settings'` is NOT new gating: it is the gate the entry had
     * under Settings, carried over so nobody gains or loses the link.
     */
    {
      label: 'Happy Logistics',
      permission: 'settings',
      icon: 'fa-solid fa-truck-fast',
      expanded: false,
      children: [
        { label: 'Setup guide', icon: 'fa-solid fa-list-check', route: '/settings/happy-logistics', queryParams: { tab: 'guide' } },
        { label: 'Connection', icon: 'fa-solid fa-plug', route: '/settings/happy-logistics', queryParams: { tab: 'connection' } },
        { label: 'Depots', icon: 'fa-solid fa-warehouse', route: '/settings/happy-logistics', queryParams: { tab: 'branches' } },
        { label: 'Deliveries', icon: 'fa-solid fa-truck', route: '/settings/happy-logistics', queryParams: { tab: 'deliveries' } },
        { label: 'Routes', icon: 'fa-solid fa-route', route: '/settings/happy-logistics', queryParams: { tab: 'routes' } },
        { label: 'Packing slips', icon: 'fa-solid fa-file-invoice', route: '/settings/happy-logistics', queryParams: { tab: 'packing' } },
        { label: 'Activity log', icon: 'fa-solid fa-clock-rotate-left', route: '/settings/happy-logistics', queryParams: { tab: 'activity' } }
      ]
    },
    /**
     * v1.6.1551 — Happy Texting is its own TOP-LEVEL section.
     *
     * It was split across two places and buried: the tabbed shell sat under
     * Marketing, and Inbox / Campaigns / Abandoned Cart sat three levels deep
     * under Settings -> Integrations, where every level is collapsed by
     * default. Neither half was findable from the other.
     *
     * Everything now hangs off one entry at level one, so each screen is a
     * single expand away.
     */
    {
      label: 'Happy Texting',
      icon: 'fa-solid fa-comment-sms',
      expanded: false,
      children: [
        { label: 'Inbox', icon: 'fa-solid fa-inbox', route: '/settings/integrations/happy-texting/inbox' },
        { label: 'Campaigns', icon: 'fa-solid fa-bullhorn', route: '/settings/integrations/happy-texting/campaigns' },
        /**
         * The existing five-tab shell: Setup, Store Mappings, SMS Templates,
         * Consents and Logs. Named for what an operator goes there to DO rather
         * than for the component.
         */
        { label: 'Mappings, Logs and Consents', icon: 'fa-solid fa-link', route: '/marketing/happy-texting' },
        { label: 'Abandoned Cart', icon: 'fa-solid fa-cart-arrow-down', route: '/settings/integrations/happy-texting/abandoned-cart' },
        // v1.6.1772 — Asana 1219731082388420: "under the happy texting menu
        // next to abandoned cart". The two are the same kind of thing — an
        // automated text that tries to recover something — so they sit
        // together rather than one hiding in Subscriptions.
        { label: 'Win-Back Texts', icon: 'fa-solid fa-rotate-left', route: '/settings/integrations/happy-texting/win-back' },
        { label: 'Setup', icon: 'fa-solid fa-plug', route: '/settings/integrations/happy-texting' }
      ]
    },
    {
      label: 'Marketing',
      permission: 'marketing',
      icon: 'fa-solid fa-bullhorn',
      expanded: false,
      children: [
        { label: 'Coupons', icon: 'fa-solid fa-tag', route: '/marketing/coupons' },
        { label: 'Gift Cards', icon: 'fa-solid fa-gift', route: '/marketing/gift-cards' },
        { label: 'Rewards & Points', icon: 'fa-solid fa-star', route: '/marketing/rewards' },
        { label: 'Refer a Friend', icon: 'fa-solid fa-user-plus', route: '/marketing/referrals' },
        {
          label: 'Push Notifications',
          icon: 'fa-solid fa-bell',
          route: '/marketing/push-notifications',
          children: [
            { label: 'All Notifications', icon: 'fa-solid fa-list', route: '/marketing/push-notifications' },
            { label: 'Sign-up Messages', icon: 'fa-solid fa-user-check', route: '/marketing/push-notifications/signup' },
            { label: 'Recurring Campaigns', icon: 'fa-solid fa-rotate', route: '/marketing/push-notifications/recurring' },
            { label: 'Notification Logs', icon: 'fa-solid fa-file-lines', route: '/marketing/push-notifications/logs' }
          ]
        },
        { label: 'Pop-ups', icon: 'fa-solid fa-window-restore', route: '/marketing/popups' },
        // v1.6.1551 — Happy Texting is TOP LEVEL now, not a Marketing child.
        // v1.6.605 — Discount Rules nav entry hidden per operator request. The
        // route at /marketing/discount-rules remains accessible by direct URL
        // for QA / testing; remove this comment + re-add the entry to surface.
        // { label: 'Discount Rules', icon: 'fa-solid fa-percent', route: '/marketing/discount-rules' }
      ]
    },
    { label: 'Media Library', permission: 'media', icon: 'fa-solid fa-images', route: '/media' },
    {
      label: 'Labels',
      permission: 'labels',
      icon: 'fa-solid fa-tags',
      expanded: false,
      children: [
        { label: 'All Labels', icon: 'fa-solid fa-list', route: '/labels' },
        { label: 'Templates', icon: 'fa-solid fa-file-lines', route: '/labels/templates' },
        { label: 'Label Settings', icon: 'fa-solid fa-sliders', route: '/labels/settings' }
      ]
    },
    {
      label: 'Settings',
      permission: 'settings',
      icon: 'fa-solid fa-gear',
      expanded: false,
      children: [
        { label: 'Onboarding Wizard', icon: 'fa-solid fa-wand-magic-sparkles', route: '/settings/onboarding' },
        { label: 'General', icon: 'fa-solid fa-sliders', route: '/settings/general' },
        { label: 'Payment Methods', icon: 'fa-solid fa-credit-card', route: '/settings/payment-methods' },
        { label: 'Shipping Wallet', icon: 'fa-solid fa-wallet', route: '/settings/shipping-wallet' },
        { label: 'Tax Rates', icon: 'fa-solid fa-calculator', route: '/settings/tax' },
        { label: 'Tip Settings', icon: 'fa-solid fa-hand-holding-dollar', route: '/settings/tips' },
        { label: 'Charging Currency', icon: 'fa-solid fa-money-bill-wave', route: '/settings/charging-currency' },
        { label: 'Countdown Settings', icon: 'fa-solid fa-clock', route: '/settings/countdown' },
        { label: 'Cutoff Rules', icon: 'fa-solid fa-calendar-xmark', route: '/settings/cutoff-rules' },
        // Catering and split-delivery stores only; the route is guarded too.
        { label: 'Split Delivery Settings', icon: 'fa-solid fa-percent', route: '/settings/split-delivery', cateringOrSplitOnly: true },
        { label: 'Picking Settings', icon: 'fa-solid fa-barcode', route: '/settings/picking' },
        // v1.6.1812 — Asana 1219891763182594. Detection runs for every store
        // whether or not anyone opens this; the page exists so staff can see
        // what applies to them and override it.
        { label: 'Sign-up Protection', icon: 'fa-solid fa-user-shield', route: '/settings/signup-protection' },
        // v1.6.1820 — Asana 1219941484883060.
        { label: '  · Remembered Shapes', icon: 'fa-solid fa-fingerprint', route: '/settings/signup-protection/shapes' },
        // v1.6.1771 — was the Pro-only fa-file-chart-line; see the note on Reports.
        // v1.6.1829 — Asana 1220010913938620. Sits beside Sign-up Protection
        // because both are bot defences, and an operator hunting one will
        // look for the other here.
        { label: 'reCAPTCHA', icon: 'fa-solid fa-shield-halved', route: '/settings/security/recaptcha' },
        { label: 'Report Settings', icon: 'fa-solid fa-table-list', route: '/settings/reports' },
        { label: 'Importers', icon: 'fa-solid fa-file-import', route: '/settings/importers' },
        { label: 'Delivery Zones', icon: 'fa-solid fa-map-marked-alt', route: '/delivery/zones' },
        { label: 'Cart Rules', icon: 'fa-solid fa-shopping-cart', route: '/delivery/cart-rules' },
        { label: 'Pickup Locations', icon: 'fa-solid fa-location-dot', route: '/delivery/pickup-locations' },
        { label: 'Holidays', icon: 'fa-solid fa-calendar-days', route: '/delivery/holidays' },
        { label: 'Pages', icon: 'fa-solid fa-file-lines', expanded: false, children: [
          { label: 'FAQ', icon: 'fa-solid fa-circle-question', route: '/settings/pages/faq' },
          { label: 'Blog Posts', icon: 'fa-solid fa-newspaper', route: '/settings/pages/posts' },
          { label: 'Privacy Policy', icon: 'fa-solid fa-shield-halved', route: '/settings/pages/privacy-policy' },
          // v1.6.1330 — Refund & Return Policy, placed directly after Privacy Policy per spec.
          { label: 'Refund & Return Policy', icon: 'fa-solid fa-rotate-left', route: '/settings/pages/refund-return-policy' },
          // v1.6.1522 — Terms of Service (Asana 1217773654728671)
          { label: 'Terms of Service', icon: 'fa-solid fa-file-contract', route: '/settings/pages/terms-of-service' },
          { label: 'Custom Pages', icon: 'fa-solid fa-file-lines', route: '/settings/pages/custom-pages' },
          // v1.6.1689 — Asana 1218589622134931. Beside Custom Pages: the two
          // share the storefront nav's single ordered list.
          { label: 'Navigation Links', icon: 'fa-solid fa-link', route: '/settings/pages/navigation-links' },
          // v1.6.1691 — the dropdowns that pages and links are filed into.
          { label: 'Navigation Dropdowns', icon: 'fa-solid fa-folder-tree', route: '/settings/pages/navigation-groups' },
          // v1.6.1695 — the product URL pattern and the Meta catalog feed.
          { label: 'Product Links & Meta Feed', icon: 'fa-solid fa-share-nodes', route: '/settings/product-links' },
          /**
           * v1.6.1770 — MOVED here from Settings → Integrations.
           *
           * v1.6.1764 filed it under Integrations, which was wrong twice
           * over: it is not an integration, and it sat about twenty-five
           * entries down a collapsed sub-group between Uber Direct and the
           * Meta pixel. The operator's report was simply "i dont see seo on
           * the side bar" — and they were looking straight at the Pages
           * group, which is exactly where its subject lives.
           *
           * It belongs beside the pages whose metadata it edits: the editor's
           * three tabs ARE these entries — site pages, custom pages and blog
           * posts.
           */
          { label: 'Search & SEO', icon: 'fa-solid fa-magnifying-glass', route: '/settings/seo/pages' },
          // v1.6.1776 — the store's web address for search engines, its page
          // list and its robots file (Asana 1219730990059024).
          { label: 'Search: Web Address', icon: 'fa-solid fa-globe', route: '/settings/seo/address' },
        ]},
        { label: 'System Emails', icon: 'fa-solid fa-envelope', route: '/settings/emails' },
        { label: 'Custom API', icon: 'fa-solid fa-key', route: '/settings/custom-api' },
        // v1.6.1771 — fa-messages is Pro; the free build spells it fa-comments.
        { label: 'Contact Messages', icon: 'fa-solid fa-comments', route: '/settings/contact-messages' },
        {
          label: 'Integrations',
          icon: 'fa-solid fa-plug',
          route: '/settings/integrations',
          children: [
            { label: 'All Integrations', icon: 'fa-solid fa-list', route: '/settings/integrations' },
            /**
             * v1.6.1588 — FLAT again, with the tabs as siblings.
             *
             * v1.6.1574 made this a group with children. It sits at nav level
             * THREE (Settings -> Integrations -> Uber Direct), and the template
             * renders level three as a plain `[routerLink]` — there is no
             * group branch and no toggle at that depth. So it rendered as a
             * link with no `route`: visible, inert, unclickable.
             *
             * The template only supports groups at levels one and two. Rather
             * than add a fourth level of nesting for six entries, the tabs are
             * listed flat beneath the parent, which reads the same in the
             * sidebar and needs no template change.
             */
            { label: 'Uber Direct', icon: 'fa-brands fa-uber', route: '/settings/integrations/uber-direct' },
            { label: '  · Opening Hours', icon: 'fa-solid fa-clock', route: '/settings/integrations/uber-direct', queryParams: { tab: 'hours' } },
            { label: '  · Webhook', icon: 'fa-solid fa-satellite-dish', route: '/settings/integrations/uber-direct', queryParams: { tab: 'webhook' } },
            { label: '  · Health Check', icon: 'fa-solid fa-heart-pulse', route: '/settings/integrations/uber-direct', queryParams: { tab: 'health' } },
            { label: '  · Action Logs', icon: 'fa-solid fa-clock-rotate-left', route: '/settings/integrations/uber-direct', queryParams: { tab: 'logs' } },
            { label: '  · API Request Logs', icon: 'fa-solid fa-code', route: '/settings/integrations/uber-direct', queryParams: { tab: 'apilogs' } },
            { label: 'MailChimp', icon: 'fa-brands fa-mailchimp', route: '/settings/integrations/mailchimp' },
            // v1.6.1331 — Klaviyo integration.
            { label: 'Klaviyo', icon: 'fa-solid fa-envelope-open-text', route: '/settings/integrations/klaviyo' },
            { label: 'Google Pixel', icon: 'fa-brands fa-google', route: '/settings/integrations/google-pixel' },
            { label: 'Meta Pixel', icon: 'fa-brands fa-facebook', route: '/settings/integrations/meta-pixel' },
            // v1.6.1462 — FedEx (Asana 1208697113381599)
            { label: 'FedEx', icon: 'fa-solid fa-box', route: '/settings/integrations/fedex' },
            // v1.6.1515 — Customer Frequency Report overrides (Asana 1218151147883808)
            { label: 'Customer Frequency Report', icon: 'fa-solid fa-user-clock', route: '/settings/frequency-report' },
            // v1.6.1638 — Asana 1218931367183430
            { label: 'Pickup Location Retention', icon: 'fa-solid fa-shop', route: '/settings/pickup-retention-report' },
            // v1.6.1642 — Asana 1218508518581275
            { label: 'Customer Credit', icon: 'fa-solid fa-wallet', route: '/settings/customer-credit' },
            // v1.6.1652 — Asana 1219098379274150
            { label: 'Social Feeds', icon: 'fa-solid fa-hashtag', route: '/settings/social-feeds' },
            // Happy Logistics (v1.6.1664) is its own top-level section now.
            // v1.6.1539 — How did you hear about us (Asana 1218326037379568)
            { label: 'How They Found Us', icon: 'fa-solid fa-bullhorn', route: '/settings/acquisition-sources' },
            // v1.6.1513 — Custom Scripts (Asana 1217185486840703)
            { label: 'Custom Scripts', icon: 'fa-solid fa-code', route: '/settings/website-scripts' },
            // v1.6.1491 — Warehouse Proximity (Asana 1217904670813879)
            { label: 'Warehouse Proximity', icon: 'fa-solid fa-location-crosshairs', route: '/settings/warehouse-proximity' },
            // v1.6.1551 — Happy Texting is its own top-level section now.
            { label: 'Push Notifications', icon: 'fa-brands fa-google', route: '/settings/integrations/push-notifications' },
            { label: 'SMS Logs', icon: 'fa-solid fa-comment-dots', route: '/settings/integrations/sms-logs' },
            { label: 'WordPress Sync', icon: 'fa-brands fa-wordpress', route: '/settings/wordpress-sync' }
          ]
        },
        { label: 'POS Integration', icon: 'fa-solid fa-cash-register', route: '/pos' }
      ]
    }
  ];

  @HostListener('window:toggle-mobile-menu')
  toggleMobile() {
    this.mobileOpen = !this.mobileOpen;
  }

  closeMobile() {
    this.mobileOpen = false;
  }

  toggleItem(item: NavItem) {
    item.expanded = !item.expanded;
  }
}
